import { useCallback } from 'react';
import { toApiError } from '@/api/client';
import { t } from '@/i18n';
import { useToast } from '@/hooks/useToast';

/**
 * Default Russian-language messages for well-known HTTP error shapes. Server
 * messages from the `ErrorEnvelope` are preferred when present.
 */
const DEFAULT_MESSAGES: Record<number, string> = { ...t.api.status };
const CODE_MESSAGES: Record<string, string> = { ...t.api.codes };

export function useApiErrorToast() {
  const { showToast } = useToast();

  return useCallback(
    (err: unknown, fallback: string = t.api.fallback) => {
      const apiErr = toApiError(err);
      // Server messages (incl. plain NestJS exception text) are English and
      // never meant for direct display. A caller-supplied fallback is more
      // specific to the situation (e.g. "wrong credentials" on a failed
      // login) than the generic per-status text, so it wins when the caller
      // customized it; otherwise fall back to the per-status default.
      const hasCustomFallback = fallback !== t.api.fallback;
      const message =
        CODE_MESSAGES[apiErr.code] ??
        (hasCustomFallback ? fallback : undefined) ??
        DEFAULT_MESSAGES[apiErr.status] ??
        fallback;
      showToast(message, 'error');
      return apiErr;
    },
    [showToast],
  );
}
